import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Spinner,
  UncontrolledDropdown,
} from "reactstrap";
import { toast } from "react-toastify";

import BreadCrumb from "Components/Common/BreadCrumb";
import Permission from "Components/Common/Permission";
import DeleteModal from "Components/Common/DeleteModal";
import { normalize } from "common/lib/normalize";
import {
  getOverlappingPayment,
  deleteOverlappingPayment,
} from "common/services/payments.service";
import {
  getOverlapStatusLabel,
  isConfirmedDuplicate,
} from "common/constants/overlap-status";

import OverlappingPaymentDetails from "../OverlappingPaymentDetails";
import ResolvePaymentModal from "../ResolvePayment";
import UpdatePayment from "../UpdatePayment";

// Mirrors the list page's populate so the detail view resolves the same
// relations the comparison tables read from.
const POPULATE = [
  "payment",
  "payment.agency",
  "payment.mda",
  "payment.mda.region",
  "payment.individual",
  "overlappingPayment",
  "overlappingPayment.agency",
  "overlappingPayment.mda",
  "overlappingPayment.mda.region",
  "overlappingPayment.individual",
];

const ViewOverlappingPayment = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response: any = await getOverlappingPayment(id, {
        populate: POPULATE,
      });
      setData(normalize(response.data));
      setNotFound(false);
    } catch (e: any) {
      setNotFound(true);
      toast.error("Error while fetching the overlapping payment");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [resolvePaymentModal, setResolvePaymentModal] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteOverlappingPayment(data?.id);
      setDeleteModal(false);
      navigate("/overlapping-payments");
      toast.success("Overlapping payment deleted successfully");
    } catch (e: any) {
      toast.error("Error while deleting the overlapping payment");
    } finally {
      setDeleting(false);
    }
  };

  const individualName =
    data?.payment?.individual?.fullName ||
    data?.overlappingPayment?.individual?.fullName ||
    "Overlapping Payment";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Overlapping Payment Details"
          pageTitles={[
            { title: "Dashboard", link: "/" },
            { title: "Overlapping Payments", link: "/overlapping-payments" },
          ]}
          back
        />

        <Row className="justify-content-center">
          <Col xxl={10}>
            <Card>
              {loading ? (
                <CardBody className="p-5 text-center">
                  <Spinner color="primary" />
                </CardBody>
              ) : notFound ? (
                <CardBody className="p-5 text-center">
                  <h5 className="fs-15 mb-1">Overlapping payment not found</h5>
                  <p className="text-muted mb-0">
                    It may have been deleted, or you may not have access to it.
                  </p>
                </CardBody>
              ) : (
                <>
                  <CardHeader className="border-bottom-dashed p-4">
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0 me-3">
                        <div className="avatar-sm">
                          <div className="avatar-title bg-primary-subtle rounded fs-24">
                            <i className="ri-file-copy-2-line text-primary"></i>
                          </div>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h5
                          className="fs-15 mb-1"
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {individualName}
                        </h5>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                          {data?.resolved ? (
                            <span className="badge bg-success-subtle text-success fs-12">
                              Resolved
                            </span>
                          ) : (
                            <span className="badge bg-danger-subtle text-danger fs-12">
                              Unresolved
                            </span>
                          )}
                          {data?.overlapStatus && (
                            <span
                              className={`badge fs-12 ${
                                isConfirmedDuplicate(data.overlapStatus)
                                  ? "bg-danger-subtle text-danger"
                                  : "bg-success-subtle text-success"
                              }`}
                            >
                              {getOverlapStatusLabel(data.overlapStatus)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <UncontrolledDropdown className="float-end">
                          <DropdownToggle
                            tag="a"
                            className="btn py-0 fs-13 material-shadow-none align-middle text-primary"
                            role="button"
                          >
                            Actions
                            <i className="ri-arrow-down-s-fill fs-16 ms-1"></i>
                          </DropdownToggle>
                          <DropdownMenu className="dropdown-menu-end">
                            {/* A record is either resolved or not, so it gets
                                exactly one of these two actions. */}
                            {data?.resolved === true ? (
                              <Permission
                                resource="overlapping-payment"
                                action="update"
                              >
                                <DropdownItem
                                  href="#"
                                  onClick={(e: any) => {
                                    e.preventDefault();
                                    setIsUpdateModalOpen(true);
                                  }}
                                >
                                  <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                                  Edit Resolution
                                </DropdownItem>
                              </Permission>
                            ) : (
                              <Permission
                                resource="overlapping-payment"
                                action="update"
                              >
                                <DropdownItem
                                  href="#"
                                  onClick={(e: any) => {
                                    e.preventDefault();
                                    setResolvePaymentModal(true);
                                  }}
                                >
                                  <i className="ri-checkbox-circle-fill align-bottom me-2 text-muted"></i>{" "}
                                  Resolve Payment
                                </DropdownItem>
                              </Permission>
                            )}
                            <Permission
                              resource="overlapping-payment"
                              action="delete"
                            >
                              <DropdownItem
                                href="#"
                                onClick={(e: any) => {
                                  e.preventDefault();
                                  setDeleteModal(true);
                                }}
                              >
                                <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                                Delete
                              </DropdownItem>
                            </Permission>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </div>
                    </div>
                  </CardHeader>

                  <CardBody className="p-4">
                    <OverlappingPaymentDetails record={data} />
                  </CardBody>
                </>
              )}
            </Card>
          </Col>
        </Row>

        <ResolvePaymentModal
          toggle={() => setResolvePaymentModal(false)}
          isModalOpen={resolvePaymentModal}
          fetchData={fetchData}
          selectedRecord={data}
          // The merge removes this overlap record, so there is nothing left
          // to show here.
          onMerged={() => navigate("/overlapping-payments")}
        />
        <UpdatePayment
          toggle={() => setIsUpdateModalOpen(false)}
          isModalOpen={isUpdateModalOpen}
          fetchData={fetchData}
          selectedRecord={data}
          // Same as resolving: a merge removes this overlap record.
          onMerged={() => navigate("/overlapping-payments")}
        />
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDelete()}
          onCloseClick={() => setDeleteModal(false)}
          loading={deleting}
        />
      </Container>
    </div>
  );
};

export default ViewOverlappingPayment;
